import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { MfaVerify } from "@/components/pages/auth/login/mfa-verify"
import {
	ResetPasswordError,
	ResetPasswordForm,
	ResetPasswordInvalid,
	ResetPasswordLoading,
	ResetPasswordSuccess,
} from "@/components/pages/auth/reset-password"
import { useMfaGate } from "@/hooks/auth/use-mfa-gate"
import { useRecoverySession } from "@/hooks/auth/use-recovery-session"
import {
	type MfaVerificationData,
	prepareMfaVerification,
	updatePassword,
	verifyMfa,
} from "@/lib/auth"
import { GENERIC_ERROR_MESSAGE } from "@/lib/constants"
import { captureAuthError, getUserFriendlyError } from "@/lib/errors"
import type { MfaCodeInput, ResetPasswordInput } from "@/lib/schemas/auth"

export const Route = createFileRoute("/_auth/reset-password")({
	component: ResetPasswordPage,
})

function ResetPasswordPage() {
	const navigate = useNavigate()
	const sessionState = useRecoverySession()
	const { gate, retry } = useMfaGate(sessionState === "valid")
	const [mfaPassed, setMfaPassed] = useState(false)
	const [refreshedVerification, setRefreshedVerification] =
		useState<MfaVerificationData | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isSuccess, setIsSuccess] = useState(false)

	const verification =
		refreshedVerification ??
		(gate.status === "required" ? gate.verification : null)

	const handleMfaVerify = async ({ totpCode }: MfaCodeInput) => {
		if (gate.status !== "required" || !verification) {
			setError(GENERIC_ERROR_MESSAGE)
			return
		}

		const { factor } = gate
		setError(null)
		setIsSubmitting(true)

		try {
			await verifyMfa(verification.factorId, verification.challengeId, totpCode)
			setMfaPassed(true)
		} catch (err) {
			captureAuthError(err)
			setError(getUserFriendlyError(err))
			try {
				setRefreshedVerification(await prepareMfaVerification(factor))
			} catch (refreshErr) {
				captureAuthError(refreshErr)
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleSubmit = async ({ password }: ResetPasswordInput) => {
		setError(null)
		setIsSubmitting(true)

		const { error } = await updatePassword(password)

		if (error) {
			captureAuthError(error)
			setError(getUserFriendlyError(error))
			setIsSubmitting(false)
			return
		}

		setIsSuccess(true)
	}

	if (sessionState === "loading") {
		return <ResetPasswordLoading />
	}

	if (sessionState === "invalid") {
		return <ResetPasswordInvalid />
	}

	if (isSuccess) {
		return <ResetPasswordSuccess onContinue={() => navigate({ to: "/" })} />
	}

	if (gate.status === "resolving") {
		return <ResetPasswordLoading />
	}

	if (gate.status === "error") {
		return <ResetPasswordError onRetry={retry} />
	}

	if (gate.status === "required" && !mfaPassed) {
		return (
			<MfaVerify
				isLoading={isSubmitting}
				error={error}
				onSubmit={handleMfaVerify}
				onBack={() => navigate({ to: "/login" })}
			/>
		)
	}

	return (
		<ResetPasswordForm
			isSubmitting={isSubmitting}
			error={error}
			onSubmit={handleSubmit}
		/>
	)
}
